import { ref, computed } from 'vue'
import { getAllowedGameIds } from '@/api/session'
import iconSSC from '@/assets/lobby/icon/SSC.png'
import iconPK10 from '@/assets/lobby/icon/PK10.png'
import iconK3 from '@/assets/lobby/icon/K3.png'
import iconKENO from '@/assets/lobby/icon/KENO.png'
import iconLHC from '@/assets/lobby/icon/LHC.png'
import icon11X5 from '@/assets/lobby/icon/11X5.png'
import iconPCB from '@/assets/lobby/icon/PCB.png'
import iconLF from '@/assets/lobby/icon/LF.png'

/** 生产上架彩种（与 backend productionGames 一致） */
export const PRODUCTION_GAME_IDS = Object.freeze([
  14, // 福彩3D
  15, // 体彩P3P5（全部游戏 + 默认收藏）
  16, // 超级大乐透
  17, // 双色球
  1103, // 天成六合彩
  19503, // 澳门飞艇
  1909, // 天成快三
  21503 // 天成快乐8
])

const PRODUCTION_SET = new Set(PRODUCTION_GAME_IDS)

/** 分类页 / 弹窗共用：各组固定彩种（顺序即展示顺序） */
export const CATEGORY_GAME_IDS = Object.freeze({
  LF: [14, 15, 17, 16], // 福彩3D、体彩P3P5、双色球、超级大乐透
  PK10: [19503], // 澳门飞艇
  LHC: [1103], // 天成六合彩
  K3: [1909], // 天成快三
  KENO: [21503] // 天成快乐8
})

/** @deprecated 兼容旧引用 */
export const LF_CATEGORY_IDS = CATEGORY_GAME_IDS.LF

const item = (data) => {
  const row = {
    badge: '',
    stars: 3,
    hot: false,
    isNew: false,
    fav: false,
    enabled: true,
    tradPlaySwitch: true,
    entPlaySwitch: true,
    players: 0,
    ...data
  }
  if (data.enabled === undefined) {
    row.enabled = PRODUCTION_SET.has(Number(data.id))
  }
  return row
}

export const isGameVisible = (g) => {
  if (!g || g.enabled === false) return false
  const allowed = getAllowedGameIds()
  if (!allowed) return true
  return allowed.includes(Number(g.id))
}

/** 递增以强制依赖方刷新（打开彩种弹窗同步后） */
export const visibilityEpoch = ref(0)

export const visibleGames = computed(() => {
  void visibilityEpoch.value
  return games.value.filter(isGameVisible)
})

export const gamesByGroup = (groupKey) => {
  const ids = CATEGORY_GAME_IDS[groupKey]
  if (Array.isArray(ids) && ids.length) {
    return ids
      .map((id) => games.value.find((g) => Number(g.id) === Number(id)))
      .filter((g) => g && isGameVisible(g))
  }
  return visibleGames.value.filter((g) => g.group === groupKey)
}

/** 投注/走势页「收藏彩种选择」：跟分类页小心心联动（我的收藏） */
const FAV_STORAGE_KEY = 'lgw-fav-game-ids'

function readStoredFavIds () {
  try {
    const raw = JSON.parse(localStorage.getItem(FAV_STORAGE_KEY) || '[]')
    if (Array.isArray(raw)) return raw.map(Number).filter((n) => n > 0)
  } catch (_) { /* ignore */ }
  return null
}

function writeStoredFavIds (ids) {
  try {
    localStorage.setItem(FAV_STORAGE_KEY, JSON.stringify(ids))
  } catch (_) { /* ignore */ }
}

/** @deprecated 保留导出；实际列表以 fav 标记为准 */
export const FAV_PICKER_IDS = [15]

/** 收藏彩种选择 = 当前已点红心且仍上架的彩种 */
export const favPickerList = () => {
  void visibilityEpoch.value
  return visibleGames.value.filter((g) => g.fav)
}

/**
 * 投注弹窗一级分类（不含「收藏彩种选择」头，由页面模板固定渲染）
 * 顺序：低频彩 → PK10 → 六合彩 → 快三 → 快乐8
 */
export const LOT_SHEET_CATS = Object.freeze([
  { key: 'LF', label: '低频彩', ids: CATEGORY_GAME_IDS.LF },
  { key: 'PK10', label: 'PK10', ids: CATEGORY_GAME_IDS.PK10 },
  { key: 'LHC', label: '六合彩', ids: CATEGORY_GAME_IDS.LHC },
  { key: 'K3', label: '快三', ids: CATEGORY_GAME_IDS.K3 },
  { key: 'KENO', label: '快乐8', ids: CATEGORY_GAME_IDS.KENO }
])

export const gamesForLotCat = (cat) => {
  if (cat && Array.isArray(cat.ids) && cat.ids.length) {
    return cat.ids
      .map((id) => games.value.find((g) => Number(g.id) === Number(id)))
      .filter((g) => g && isGameVisible(g))
  }
  return gamesByGroup(cat?.key)
}

/** 分组里至少有一个可展示彩种才显示该分组入口 */
export const visibleLotCats = (cats) =>
  (cats || []).filter((cat) => {
    if (Array.isArray(cat.ids) && cat.ids.length) {
      return cat.ids.some((id) => {
        const g = games.value.find((x) => Number(x.id) === Number(id))
        return g && isGameVisible(g)
      })
    }
    return visibleGames.value.some((g) => g.group === cat.key)
  })

export async function syncGamePlaySwitches (gameId) {
  try {
    const res = await fetch(`/lgw/games/${gameId}/setting`).then((r) => r.json())
    if (!res?.success || !res.data) return false
    const g = games.value.find((x) => Number(x.id) === Number(gameId))
    if (!g) return false
    if ('tradPlaySwitch' in res.data) g.tradPlaySwitch = !!res.data.tradPlaySwitch
    if ('entPlaySwitch' in res.data) g.entPlaySwitch = res.data.entPlaySwitch !== false
    visibilityEpoch.value += 1
    return true
  } catch (_) {
    return false
  }
}

export async function syncGameVisibility () {
  try {
    const res = await fetch('/lgw/games/visibility').then((r) => r.json())
    if (!res?.success || !Array.isArray(res.data?.games)) return false
    const map = new Map(
      res.data.games.map((g) => {
        const row = { id: Number(g.id) }
        if ('enabled' in g) row.enabled = !!g.enabled
        if ('hot' in g) row.hot = !!g.hot
        if ('isNew' in g) row.isNew = !!g.isNew
        if ('tradPlaySwitch' in g) row.tradPlaySwitch = !!g.tradPlaySwitch
        if ('entPlaySwitch' in g) row.entPlaySwitch = g.entPlaySwitch !== false
        return [row.id, row]
      })
    )
    games.value.forEach((g) => {
      const row = map.get(Number(g.id))
      if (!row) {
        // 后端未返回的彩种：按生产清单兜底
        g.enabled = PRODUCTION_SET.has(Number(g.id))
        return
      }
      if ('enabled' in row) g.enabled = row.enabled
      if ('hot' in row) g.hot = row.hot
      if ('isNew' in row) g.isNew = row.isNew
      if ('tradPlaySwitch' in row) g.tradPlaySwitch = row.tradPlaySwitch
      if ('entPlaySwitch' in row) g.entPlaySwitch = row.entPlaySwitch
    })
    visibilityEpoch.value += 1
    return true
  } catch (_) {
    return false
  }
}

export const games = ref([
  item({ id: 307, name: '腾讯分分彩', group: 'SSC', badge: '金选彩票', period: '1分钟/期', stars: 5, hot: true, fav: true, players: 225400, icon: iconSSC }),
  item({ id: 104, name: '天成分分彩', group: 'SSC', badge: '中奖推荐排行', period: '1分钟/期', stars: 5, hot: true, fav: true, players: 234000, icon: iconSSC }),
  item({ id: 106, name: '天成5分彩', group: 'SSC', badge: '中奖推荐排行', period: '5分钟/期', stars: 4, fav: true, players: 18600, icon: iconSSC }),
  item({ id: 5303, name: '极速时时彩', group: 'SSC', badge: '热门销量彩票', period: '1.25分钟/期', stars: 4, hot: true, icon: iconSSC }),
  item({ id: 7307, name: '澳洲幸运5', group: 'SSC', badge: '热门销量彩票', period: '5分钟/期', stars: 4, hot: true, icon: iconSSC }),
  item({ id: 8204, name: '加拿大幸运5', group: 'SSC', badge: '金选彩票', period: '3.5分钟/期', stars: 3, icon: iconSSC }),
  item({ id: 1502, name: '双子五分彩', group: 'SSC', badge: '金选彩票', period: '5分钟/期', stars: 3, icon: iconSSC }),
  item({ id: 10007, name: '阿里分分彩', group: 'SSC', badge: '金选彩票', period: '1分钟/期', stars: 3, icon: iconSSC }),
  item({ id: 8205, name: '台湾幸运5', group: 'SSC', badge: '金选彩票', period: '5分钟/期', stars: 3, icon: iconSSC }),
  item({ id: 107, name: '天成1.5分彩', group: 'SSC', badge: '中奖推荐排行', period: '1.5分钟/期', stars: 4, hot: true, icon: iconSSC }),
  item({ id: 19903, name: '秒秒时时彩', group: 'SSC', badge: '热门销量彩票', period: '即投即开', stars: 4, hot: true, icon: iconSSC }),
  item({ id: 20419, name: 'EOS阳光时时彩', group: 'SSC', badge: '金选彩票', period: '2分钟/期', stars: 3, hot: true, icon: iconSSC }),
  item({ id: 903, name: '天秤分分彩', group: 'SSC', badge: '金选彩票', period: '1分钟/期', stars: 4, hot: true, icon: iconSSC }),
  item({ id: 7203, name: '腾讯3分彩', group: 'SSC', badge: '热门销量彩票', period: '3分钟/期', stars: 3, icon: iconSSC }),
  item({ id: 7204, name: '腾讯5分彩', group: 'SSC', badge: '热门销量彩票', period: '5分钟/期', stars: 3, icon: iconSSC }),
  item({ id: 5603, name: '天蝎五分彩', group: 'SSC', badge: '金选彩票', period: '5分钟/期', stars: 3, icon: iconSSC }),
  item({ id: 7205, name: '腾讯10分彩', group: 'SSC', badge: '热门销量彩票', period: '10分钟/期', stars: 3, icon: iconSSC }),
  item({ id: 8206, name: '加拿大卑斯彩', group: 'SSC', badge: '金选彩票', period: '3.5分钟/期', stars: 3, icon: iconSSC }),

  item({ id: 103, name: '天成11选5', group: '11X5', period: '5分钟/期', stars: 3, icon: icon11X5 }),
  item({ id: 20215, name: '秒秒11选5', group: '11X5', badge: '金选彩票', period: '即投即开', stars: 3, hot: true, icon: icon11X5 }),
  item({ id: 5704, name: '极速11选5', group: '11X5', period: '1.25分钟/期', stars: 3, icon: icon11X5 }),
  item({ id: 902, name: '双鱼11选5', group: '11X5', badge: '金选彩票', period: '2分钟/期', stars: 3, hot: true, icon: icon11X5 }),
  item({ id: 20509, name: 'EOS阳光11选5', group: '11X5', period: '2分钟/期', stars: 3, hot: true, icon: icon11X5 }),

  item({ id: 14, name: '福彩3D', group: 'LF', badge: '热门销量彩票', period: '1天/期', stars: 4, hot: true, players: 18000, icon: iconLF }),
  item({ id: 16, name: '超级大乐透', group: 'LF', badge: '热门销量彩票', period: '1,3,6/每周', stars: 4, hot: true, isNew: true, players: 15000, icon: iconLF }),
  item({ id: 17, name: '双色球', group: 'LF', badge: '热门销量彩票', period: '2,4,0/每周', stars: 4, hot: true, isNew: true, players: 18000, icon: iconLF }),
  item({ id: 15, name: '体彩P3P5', group: 'LF', period: '1天/期', stars: 4, hot: true, fav: true, players: 4114, icon: iconLF }),
  item({ id: 19804, name: '美国午间3D', group: 'LF', period: '30分钟/期', stars: 3, icon: iconLF }),
  item({ id: 19803, name: '美国晚间3D', group: 'LF', period: '30分钟/期', stars: 3, icon: iconLF }),
  item({ id: 16803, name: '台湾四星彩', group: 'LF', period: '1,2,3,4,5,6/每周', stars: 4, icon: iconLF }),
  item({ id: 10906, name: '海南四星彩', group: 'LF', period: '2,5,7/每周', stars: 4, icon: iconLF }),
  item({ id: 16804, name: '新加坡万字票', group: 'LF', period: '3,6,7/每周', stars: 4, icon: iconLF }),
  item({ id: 11103, name: 'TCG四星彩', group: 'LF', period: '5分钟/期', stars: 3, icon: iconLF }),

  item({ id: 1103, name: '天成六合彩', group: 'LHC', period: '2,4,6/每周 21:30', stars: 3, hot: true, players: 5000, icon: iconLHC }),
  item({ id: 5806, name: '极速六合彩', group: 'LHC', period: '5分钟/期', stars: 3, icon: iconLHC }),
  item({ id: 14603, name: '台湾大乐透', group: 'LHC', badge: '金选彩票', period: '2,5/每周', stars: 3, icon: iconLHC }),
  item({ id: 16805, name: '新加坡多多', group: 'LHC', period: '1,4/每周', stars: 3, icon: iconLHC }),
  item({ id: 25, name: '香港六合彩', group: 'LHC', badge: '金选彩票', period: '2,4,6/每周', stars: 3, hot: true, icon: iconLHC }),
  item({ id: 20104, name: '秒秒六合彩', group: 'LHC', badge: '金选彩票', period: '即投即开', stars: 3, icon: iconLHC }),
  item({ id: 19606, name: '水瓶六合彩', group: 'LHC', badge: '金选彩票', period: '5分钟/期', stars: 3, icon: iconLHC }),
  item({ id: 18903, name: '澳门六合彩', group: 'LHC', badge: '热门销量彩票', period: '1天/期', stars: 3, hot: true, icon: iconLHC }),

  item({ id: 1802, name: '射手赛车', group: 'PK10', badge: '金选彩票', period: '1.5分钟/期', stars: 5, hot: true, players: 710600, icon: iconPK10 }),
  item({ id: 10403, name: '天成飞艇', group: 'PK10', badge: '中奖推荐排行', period: '1分钟/期', stars: 5, hot: true, players: 455400, icon: iconPK10 }),
  item({ id: 19503, name: '澳门飞艇', group: 'PK10', badge: '金选彩票', period: '5分钟/期', stars: 4, hot: true, players: 87400, icon: iconPK10 }),
  item({ id: 5304, name: '极速赛车', group: 'PK10', badge: '热门销量彩票', period: '1.25分钟/期', stars: 4, hot: true, players: 9408, icon: iconPK10 }),
  item({ id: 7321, name: '澳洲幸运10', group: 'PK10', badge: '热门销量彩票', period: '5分钟/期', stars: 4, hot: true, icon: iconPK10 }),
  item({ id: 5706, name: '极速飞艇', group: 'PK10', badge: '热门销量彩票', period: '1.25分钟/期', stars: 4, hot: true, icon: iconPK10 }),
  item({ id: 105, name: '天成PK10', group: 'PK10', badge: '中奖推荐排行', period: '5分钟/期', stars: 3, icon: iconPK10 }),
  item({ id: 19904, name: '秒秒PK10', group: 'PK10', badge: '金选彩票', period: '即投即开', stars: 3, hot: true, icon: iconPK10 }),
  item({ id: 20510, name: 'EOS阳光赛车', group: 'PK10', badge: '金选彩票', period: '2分钟/期', stars: 3, hot: true, icon: iconPK10 }),
  item({ id: 19504, name: '新幸运飞艇', group: 'PK10', period: '5分钟/期', stars: 3, icon: iconPK10 }),
  item({ id: 19607, name: '巨蟹快艇', group: 'PK10', period: '5分钟/期', stars: 3, icon: iconPK10 }),
  item({ id: 8207, name: '加拿大卑斯赛车', group: 'PK10', period: '3.5分钟/期', stars: 3, icon: iconPK10 }),
  item({ id: 21420, name: '台湾宾果赛车', group: 'PK10', period: '5分钟/期', stars: 3, icon: iconPK10 }),
  item({ id: 21421, name: '北京福彩赛车', group: 'PK10', period: '1天/期', stars: 3, icon: iconPK10 }),
  item({ id: 21422, name: '加拿大西部赛车', group: 'PK10', period: '5分钟/期', stars: 3, icon: iconPK10 }),
  item({ id: 21423, name: '新西兰赛车', group: 'PK10', period: '4期/天', stars: 3, icon: iconPK10 }),
  item({ id: 21424, name: '斯洛伐克赛车', group: 'PK10', period: '2分钟/期', stars: 3, icon: iconPK10 }),
  item({ id: 21425, name: '希腊赛车', group: 'PK10', period: '5分钟/期', stars: 3, icon: iconPK10 }),

  item({ id: 1405, name: '天成28', group: 'PCB', badge: '中奖推荐排行', period: '1分钟/期', stars: 4, hot: true, icon: iconPCB }),
  item({ id: 20216, name: '秒秒28', group: 'PCB', badge: '金选彩票', period: '即投即开', stars: 3, icon: iconPCB }),
  item({ id: 4004, name: '加拿大PK28', group: 'PCB', badge: '金选彩票', period: '3.5分钟/期', stars: 4, hot: true, icon: iconPCB }),
  item({ id: 1402, name: '台湾28', group: 'PCB', badge: '金选彩票', period: '5分钟/期', stars: 3, hot: true, icon: iconPCB }),
  item({ id: 1404, name: '加拿大28', group: 'PCB', badge: '金选彩票', period: '3.5分钟/期', stars: 4, hot: true, icon: iconPCB }),
  item({ id: 19605, name: '狮子28', group: 'PCB', badge: '金选彩票', period: '5分钟/期', stars: 3, icon: iconPCB }),
  item({ id: 6606, name: '腾讯28', group: 'PCB', badge: '金选彩票', period: '1分钟/期', stars: 3, icon: iconPCB }),
  item({ id: 1504, name: '双子28', group: 'PCB', badge: '金选彩票', period: '5分钟/期', stars: 3, icon: iconPCB }),
  item({ id: 20803, name: 'EOS阳光28', group: 'PCB', badge: '金选彩票', period: '2分钟/期', stars: 3, hot: true, icon: iconPCB }),
  item({ id: 21426, name: '希腊28', group: 'PCB', period: '5分钟/期', stars: 3, icon: iconPCB }),
  item({ id: 21427, name: '斯洛伐克28', group: 'PCB', period: '2分钟/期', stars: 3, icon: iconPCB }),
  item({ id: 21428, name: '新西兰28', group: 'PCB', period: '4期/天', stars: 3, icon: iconPCB }),
  item({ id: 1406, name: '台湾PK28', group: 'PCB', period: '5分钟/期', stars: 3, icon: iconPCB }),
  item({ id: 18209, name: '极速28', group: 'PCB', badge: '热门销量彩票', period: '1.25分钟/期', stars: 3, icon: iconPCB }),

  item({ id: 1909, name: '天成快三', group: 'K3', period: '20分钟/期', stars: 4, hot: true, tradPlaySwitch: true, entPlaySwitch: true, players: 122400, icon: iconK3 }),
  item({ id: 19603, name: '白羊快三', group: 'K3', period: '5分钟/期', stars: 3, icon: iconK3 }),
  item({ id: 20103, name: '秒秒快三', group: 'K3', period: '即投即开', stars: 3, icon: iconK3 }),
  item({ id: 5705, name: '极速快三', group: 'K3', badge: '热门销量彩票', period: '1.25分钟/期', stars: 3, hot: true, icon: iconK3 }),
  item({ id: 5604, name: '金牛快三', group: 'K3', badge: '金选彩票', period: '45秒/期', stars: 4, icon: iconK3 }),
  item({ id: 20606, name: 'EOS阳光快三', group: 'K3', period: '2分钟/期', stars: 3, icon: iconK3 }),

  item({ id: 21503, name: '天成快乐8', group: 'KENO', badge: '新增彩票', period: '5分钟/期', stars: 4, hot: true, isNew: true, players: 95758, icon: iconKENO }),
  item({ id: 21410, name: '加拿大快乐8', group: 'KENO', badge: '新增彩票', period: '3.5分钟/期', stars: 3, hot: true, isNew: true, icon: iconKENO }),
  item({ id: 21412, name: '北京福彩快乐8', group: 'KENO', badge: '新增彩票', period: '1天/期', stars: 4, isNew: true, icon: iconKENO }),
  item({ id: 21411, name: '台湾宾果', group: 'KENO', badge: '新增彩票', period: '5分钟/期', stars: 3, hot: true, isNew: true, icon: iconKENO }),
  item({ id: 21416, name: '希腊快乐8', group: 'KENO', badge: '新增彩票', period: '5分钟/期', stars: 3, isNew: true, icon: iconKENO }),
  item({ id: 21414, name: '新西兰快乐8', group: 'KENO', badge: '新增彩票', period: '4期/天', stars: 3, isNew: true, icon: iconKENO }),
  item({ id: 21415, name: '斯洛伐克快乐8', group: 'KENO', badge: '新增彩票', period: '2分钟/期', stars: 4, isNew: true, icon: iconKENO }),
  item({ id: 21413, name: '加拿大西部快乐8', group: 'KENO', badge: '新增彩票', period: '5分钟/期', stars: 3, hot: true, isNew: true, icon: iconKENO })
])

export const tickerList = [
  '*622 福彩3D 中奖3136U',
  '*750 澳门飞艇 中奖 1980U',
  '*729 天成快三 中奖 29700U',
  '*730 天成快乐8 中奖 3465U',
  '*412 双色球 中奖 9900U',
  '*517 天成六合彩 中奖 7834U',
  '*6zk 超级大乐透 中奖 6890U'
]

export const toggleFav = (game) => {
  const target = games.value.find((g) => Number(g.id) === Number(game.id))
  if (!target || !isGameVisible(target)) return
  target.fav = !target.fav
  writeStoredFavIds(
    games.value.filter((g) => g.fav && isGameVisible(g)).map((g) => Number(g.id))
  )
  visibilityEpoch.value += 1
}

/** 用 localStorage 覆盖默认 fav；首次/升级时默认收藏体彩P3P5 */
function hydrateFavFromStorage () {
  const stored = readStoredFavIds()
  const seedKey = 'lgw-fav-seed-v2'
  let seeded = false
  try {
    seeded = localStorage.getItem(seedKey) === '1'
  } catch (_) { /* ignore */ }

  if (stored) {
    const set = new Set(stored)
    if (!seeded) {
      set.add(15)
      writeStoredFavIds([...set])
      try {
        localStorage.setItem(seedKey, '1')
      } catch (_) { /* ignore */ }
    }
    games.value.forEach((g) => {
      g.fav = set.has(Number(g.id))
    })
    return
  }

  // 无缓存：保留数据源默认 fav，并落盘
  writeStoredFavIds(
    games.value.filter((g) => g.fav && isGameVisible(g)).map((g) => Number(g.id))
  )
  try {
    localStorage.setItem(seedKey, '1')
  } catch (_) { /* ignore */ }
}

hydrateFavFromStorage()
